import { create } from 'zustand';
import { createJSONStorage, persist, subscribeWithSelector } from 'zustand/middleware';
import { withDevtools } from '@/lib/utils.ts';
import type { User } from '@/features/user/types.ts';
import type { Role } from '@/features/roles/roles.ts';

export type AuthStatus = 'idle' | 'authenticated' | 'unauthenticated' | 'loading';

export type UserState = {
  user: User | null;
  status: AuthStatus;
  hydrated: boolean;
  needsInteraction: boolean;
  setStatus: (status: AuthStatus) => void;
  setUser: (user: User | null) => void;
  patchUser: (partial: Partial<User>) => void;
  clearUser: () => void;
  setHydrated: (hydrated: boolean) => void;
  setNeedsInteraction: (needsInteraction: boolean) => void;
};

const STORAGE_KEY = 'userStore.v1';

const EMPTY_ROLES: ReadonlyArray<Role> = Object.freeze([]);

const selectStatus = (s: UserState) => s.status;
const selectDisplayName = (s: UserState) => s.user?.firstName ?? 'N/A';
const selectEmail = (s: UserState) => s.user?.email ?? 'N/A';

const ssrSafeStorage =
  typeof window !== 'undefined'
    ? createJSONStorage(() => localStorage)
    : createJSONStorage(() => ({}) as unknown as Storage);

export const useUserStore = create<UserState>()(
  withDevtools(
    subscribeWithSelector(
      persist(
        (set, get) => ({
          user: null,
          status: 'idle',
          hydrated: false,
          needsInteraction: false,
          setStatus: (status: AuthStatus) => set({ status }, false),
          setUser: (user: User | null) =>
            set({ user, status: user ? 'authenticated' : 'unauthenticated' }),
          patchUser: (partial: Partial<User>) => {
            const current = get().user;
            if (!current) return;
            set({ user: { ...current, ...partial } }, false);
          },
          clearUser: () => set({ user: null, status: 'unauthenticated' }, false),
          setHydrated: (hydrated: boolean) => set({ hydrated }, false),
          setNeedsInteraction: (needsInteraction: boolean) => set({ needsInteraction }, false),
        }),
        {
          name: STORAGE_KEY,
          version: 1,
          storage: ssrSafeStorage,
          partialize: (s: UserState) => ({
            user: s.user ? { ...s.user, roles: [] } : null,
          }),
          onRehydrateStorage: () => (state, error) => {
            if (error) {
              try {
                localStorage.removeItem(STORAGE_KEY);
              } catch {
                // ignore
              }
            }
            state?.setHydrated(true);
          },
        }
      )
    ),
    { name: 'UserStore' }
  )
);

const normRole = (x: string) => x.toUpperCase().replace(/^ROLE_/, '');
export const makeSelectHasRole = (role: string) => (s: UserState) =>
  (s.user?.roles ?? EMPTY_ROLES).some((r) => normRole(r.name) === normRole(role));

export const useAuthStatus = () => useUserStore(selectStatus);
export const useDisplayName = () => useUserStore(selectDisplayName);
export const useEmail = () => useUserStore(selectEmail);
export const useHasRoleFromUser = (role: string) => useUserStore(makeSelectHasRole(role));

export const hasRoleNow = (role: string) => makeSelectHasRole(role)(useUserStore.getState());
